import { DateObject, useCountdown } from '@capsicum/common';
import { useEffect } from 'react';

type UseReverseAuctionProps = {
	decreasingPrice: number;
	endingPrice: number;
	price: number;
	refetch: () => void;
	startingDate: DateObject;
	timeIntervalInSeconds: number;
};

// useReverseAuction is a hook that will handle the logic of the reverse auction.
// it should return the current price, the next bid countdown and the function to bid.
export const useReverseAuction = ({
	decreasingPrice,
	endingPrice,
	price,
	refetch,
	startingDate,
	timeIntervalInSeconds
}: UseReverseAuctionProps) => {
	const minutes = Math.floor(timeIntervalInSeconds / 60);
	const seconds = Math.round(timeIntervalInSeconds * 60);

	const isTimerFinished = price <= endingPrice;

	const decreasePrice = () => {
		price -= decreasingPrice;

		refetch();
	};

	const { formattedTime, reset, start, stop } = useCountdown({
		immediate: false,
		initWithDuration: true,
		minutes,
		onElapsed() {
			decreasePrice();

			if (price > endingPrice) {
				reset();
			} else if (isTimerFinished) {
				stop();
			}
		},
		seconds
	});

	// This will handle the delayed - or not - start of the countdown
	useEffect(() => {
		const diff = startingDate.getTime() - DateObject.now();

		if (diff <= 0) {
			start();
		} else {
			setTimeout(() => start(), diff);
		}
	}, [startingDate, start, stop]);

	return { countdown: formattedTime, isTimerFinished, price, start };
};
